import React, { useCallback, useMemo, useState } from 'react'
import { AppTitlePage } from '../../../components/AppTitlePage'
import { AppBtnColor } from '../../../components/btn/AppBtn'
import { AllRoutes } from '../../../core/routes/AllRoutes'
import { useNavigate } from 'react-router'
import { AppTable } from '../../../components/AppTable/AppTable'
import { IChooseItems } from '../../../components/mainContent/TableAllDishes'
import { useGetBuffetMilletQuery } from '../../../core/api/BuffetMilletApi'
import { AppTableImageViewer } from '../../../components/AppTableImageViewer/AppTableImageViewer'
import {
  IBuffetMilletDto,
  IBuffetMilletRequest,
} from '../../../core/api/dto/BuffetMilletDto'
import { useParamsControl } from '../../../utils/useParamsControl'
import { AppLoader, LoaderType } from 'components/AppLoader'
import { AppFilterInput } from '../../../components/AppInput/AppFilterInput'
import {
  AppNotification,
  NotificationType,
} from '../../../components/notification/Notification'
import { FileUploadMilletPopup } from './components/FileUploadMilletPopup'
import { GetFile } from '../../../utils/getFile'
import { BASE_URL } from '../../../core/api/BaseApi'
import { ImagePreviewWrapperSizeEnum } from 'UiKitComponents/ImagePreviewWrapper/ImagePreviewWrapper'
import {
  AppTableAlignType,
  AppTableLink,
} from '../../../components/AppTable/components/AppTableLink'
import { AppTableNumberBlock } from '../../../components/AppTable/components/AppTableNumberBlock'
import { useTranslation } from 'react-i18next'
import { useSentMilletOnFotobox } from './hooks/useSentMilletOnFotobox'
import { AppStationPopup } from '../../../entities/AppStationPopup/AppStationPopup'
import { AppDeleteFromMainServerPopup } from '../../../entities/AppDeleteFromMainServerPopup/AppDeleteFromMainServerPopup'
import { useDeleteMillet } from './hooks/useDeleteMillet'
import { AppFilterEdgeServersList } from '../../../entities/AppFilterEdgeServersList/AppFilterEdgeServersList'

const headerData = [
  { title: 'Наименование', colWidth: '65%', sort: 'millet_name' },
  { title: 'Вес, кг', colWidth: '15%', sort: 'millet_weight' },
  { title: 'Фото', colWidth: '15%' },
]

export const MilletPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const pageParams = useParamsControl<
    IBuffetMilletRequest,
    keyof IBuffetMilletRequest
  >({
    withPagination: true,
    whiteList: ['search', 'archived', 'ordering', 'edge_servers'],
  })
  const {
    data: millets,
    isLoading: isLoadingGet,
    isFetching: isFetchingGet,
  } = useGetBuffetMilletQuery({ ...pageParams }, { skip: !pageParams })
  const [chooseItems, setChooseItems] = useState<IChooseItems | null>(null)
  const [uploadFilePopup, setUploadFilePopup] = useState<boolean>(false)
  const [isLoadingLocal, setIsLoadingLocal] = useState<boolean>(false)
  const [tableData, setTableData] = useState<IBuffetMilletDto[]>([])

  const {
    sentPopup,
    openSentPopup,
    onSendMillet,
    onDeleteMilletStation,
    deleteStations,
    isDeleteMode,
    closeSentPopup,
    openDeleteStationPopup,
    isLoadingSend,
  } = useSentMilletOnFotobox({ tableData, chooseItems, setChooseItems })
  const {
    deletePopup,
    closeDeletePopup,
    onDeletePopupHandler,
    onDeleteHandler,
  } = useDeleteMillet(chooseItems, setChooseItems)

  const openUploadFilePopupHandler = useCallback(() => {
    setUploadFilePopup(true)
  }, [])

  const unloadMilletsHandler = useCallback(() => {
    setIsLoadingLocal(true)
    const params =
      pageParams && pageParams?.search ? `?search=${pageParams?.search}` : ''
    GetFile(`${BASE_URL}/buffet_millet/export_millets_to_file/${params}`)
      .then(() =>
        AppNotification({
          msg: t('Выбранные тары успешно выгружены!'),
          type: NotificationType.success,
        }),
      )
      .finally(() => setIsLoadingLocal(false))
  }, [pageParams])

  const actions = useMemo(
    () => [
      {
        btnTitle: 'Загрузить',
        btnIco: 'import',
        onClick: openUploadFilePopupHandler,
        color: AppBtnColor.whiteOutlineViolet,
      },
      {
        btnTitle: 'Выгрузить',
        btnIco: 'export',
        onClick: unloadMilletsHandler,
        color: AppBtnColor.whiteOutlineViolet,
      },
      {
        btnTitle: 'Добавить тару',
        btnIco: 'add',
        onClick: () => navigate(AllRoutes.addMillet.path),
        color: AppBtnColor.whiteOutlineViolet,
      },
    ],
    [openUploadFilePopupHandler, unloadMilletsHandler, navigate],
  )

  const tableActions = [
    {
      title: 'Удалить',
      ico: 'basket',
      onClick: onDeletePopupHandler,
    },
    ...(process.env.REACT_APP_TYPE_SERVER === 'MainServer'
      ? [
          {
            title: 'Отправить на станцию',
            ico: 'add',
            onClick: openSentPopup,
          },
        ]
      : []),
  ]

  if (!millets && isLoadingGet) {
    return <AppLoader loaderType={LoaderType.main} />
  }

  if (!millets) {
    return null
  }

  return (
    <React.Fragment>
      {isLoadingLocal && <AppLoader loaderType={LoaderType.main} />}
      {uploadFilePopup && (
        <FileUploadMilletPopup setActive={setUploadFilePopup} />
      )}
      {deletePopup && (
        <AppDeleteFromMainServerPopup
          onClose={closeDeletePopup}
          onDelete={onDeleteHandler}
          onOpenStationPopup={openDeleteStationPopup}
        />
      )}
      {sentPopup && (
        <AppStationPopup
          onClose={closeSentPopup}
          isLoading={isLoadingSend}
          onSendItems={onSendMillet}
          isDeleteMode={isDeleteMode}
          deleteStations={deleteStations}
          onDeleteStationDishes={onDeleteMilletStation}
        />
      )}
      <div className="width-for-page-wrapper">
        <AppTitlePage title="Тары" actions={actions} />
        <div className="col-7-width-from-1000">
          <div className="filter-block no-wrap mt-20">
            <AppFilterInput
              label="Поиск"
              placeholder="Например: тарелка"
              searchParam={'search'}
              ico={'search'}
              fullWidth
            />
            <AppFilterEdgeServersList fullWidth />
          </div>
          <AppTable
            data={millets}
            headerData={headerData}
            idName={'millet'}
            setTableData={setTableData}
            tableDataSelectors={[
              {
                renderItem: (item) => {
                  return (
                    <AppTableLink
                      path={`${item.id}`}
                      deleteItem={item.archived}
                    >
                      {item?.milletName}
                    </AppTableLink>
                  )
                },
              },
              {
                renderItem: (item) => {
                  return (
                    <AppTableLink
                      path={`${item.id}`}
                      deleteItem={item.archived}
                      align={AppTableAlignType.right}
                    >
                      <AppTableNumberBlock
                        value={
                          item?.milletWeight
                            .toFixed(3)
                            .toString()
                            .replace(/[.]/g, ',') || ''
                        }
                      />
                    </AppTableLink>
                  )
                },
              },
              {
                renderItem: (item) => (
                  <AppTableImageViewer
                    image={item.milletPhotoOnS3 || item.milletPhoto || null}
                    imageName={item.milletName}
                    archived={item.archived}
                    size={ImagePreviewWrapperSizeEnum.MEDIUM}
                  />
                ),
              },
            ]}
            chooseMode
            setChooseItems={setChooseItems}
            chooseItems={chooseItems}
            chooseActions={tableActions}
            isLoading={isLoadingGet || isFetchingGet}
          />
        </div>
      </div>
    </React.Fragment>
  )
}
